<script setup lang="ts">
const props = defineProps<{
  expanded?: boolean
  item: any
}>()

const emit = defineEmits(['clicked'])

const route = useRoute()

const isActive = computed(() => {
  if (!props.item?.activePath) {
    return false
  }

  return route.path.startsWith(props.item.activePath)
})

const buttonRef = ref<HTMLElement>()
const isOpen = ref(isActive.value || props.item.isOpen)

function onDropClick() {
  if (props.item.isOpen) {
    emit('clicked')
    return
  }
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    buttonRef.value?.blur()
  }
  emit('clicked')
}

defineExpose({
  isOpen,
  item: props.item
})

</script>

<template>
  <div class="group">
    <button :class="props.expanded ? 'gap-4 px-4' : 'px-2 justify-center'" @click.stop.prevent="onDropClick"
      class="nui-focus text-muted-500 dark:text-muted-400/80 hover:bg-muted-100 dark:hover:bg-muted-700/60 hover:text-muted-600 dark:hover:text-muted-200 flex w-full cursor-pointer items-center rounded-lg py-3 transition-colors duration-300"
      ref="buttonRef">
      <Icon :class="[item.icon.class, isActive && 'text-primary-500']" :name="item.icon.name" class="shrink-0"
        size="30px" />
      <span :class="[
      isActive && 'text-primary-500',
      !props.expanded ? 'hidden' : 'block',
    ]" class="block whitespace-nowrap font-sans text-sm">
        {{ item.name }}
      </span>
      <span :class="!props.expanded ? 'hidden' : 'flex'" class="ms-auto items-center justify-center"
        v-if="!props.item.isOpen">
        <Icon :class="!isOpen ? 'rotate-180' : ''" class="h-4 w-4 transition-transform duration-200"
          name="lucide:chevron-up" />
      </span>
    </button>

    <Transition 
      enter-active-class="transition-[max-height] duration-300 ease-out"
      enter-from-class="max-h-0"
      enter-to-class="max-h-96"
      leave-active-class="transition-[max-height] duration-75 ease-in"
      leave-from-class="max-h-96"
      leave-to-class="max-h-0"
    >
      <ul class="border-muted-200 relative block ps-4 overflow-hidden" v-if="props.expanded && isOpen">
        <li :key="child.to" class="border-muted-300 dark:border-muted-700 ms-2 border-s-2 first:mt-2"
          v-for="child in props.item.children">
          <NuxtLink :to="child.to"
            class="nui-focus text-muted-500 hover:text-muted-600 dark:text-muted-400/80 dark:hover:text-muted-200 relative -start-0.5 flex cursor-pointer items-center gap-2 border-s-2 border-transparent py-2 ps-4 transition-colors duration-300"
            exact-active-class="!border-primary-500 !text-primary-500 dark:!text-primary-500">
            <Icon :class="child.icon.class" :name="child.icon.name" class="shrink-0" size="30px" />
            <span :class="[!props.expanded ? 'hidden' : 'block']" class="whitespace-nowrap font-sans text-[0.85rem]">
              {{ child.name }}
            </span>
          </NuxtLink>
        </li>
      </ul>
    </Transition>
  </div>
</template>
